import type { TFunction } from "i18next";
import {
  NotificationDeliveryStatus,
  AccountingTemplateCategoryEnum,
  AnlassCampaignStatusEnum,
  AnlassTypeEnum,
  AttrTypes,
  BankAccountType,
  BankSepaEnum,
  BankTransactionStatusEnum,
  CalculateTypeEnum,
  CalculationType,
  DocumentTypes,
  DownloadCustomersOptionEnum,
  EmailContactType,
  PhoneContactType,
  PriceListItemCalculateType,
  ProductStatusEnum,
  ProductStockFlagEnum,
  ProductType,
  UnitTypeEnum,
  VariantStatusEnum,
  MarketplaceTypeEnum,
  NotificationRouteTypeEnum,
  NotificationChannel,
  RecurringScheduleStatusEnum,
  RecurringScheduleIntervalEnum,
  MarketplaceSubTypeEnum,
  StockMoveOperationEnum,
  AccountingAccountCategoryEnum,
  AccountingAccountTypeEnum,
  CarrierTypeEnum,
  PicklistStatusEnum,
  CampaignTypeEnum,
  CampaignStatusEnum,
} from "@exonn/js-sdk";
import { ResourceEnum } from "src/constants/constants.ts";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";
import { OrderStatusEnum } from "src/admin/modules/order/constants.ts";
import { PriceListStatus } from "src/admin/modules/price-list/forms/price-list/shared/types.ts";
import { enumValues } from "@exonn/utils";
import { EPOST_LETTER_STATUSES } from "src/admin/modules/epost-letter/types.ts";

const ENUM_VALUE_MAP: Record<string, Record<string, string[]>> = {
  [ResourceEnum.DeliveryTariff]: {
    calculate_type_1: enumValues(CalculateTypeEnum),
    calculate_type_2: enumValues(CalculateTypeEnum),
    calculate_type: enumValues(CalculateTypeEnum),
  },
  [ResourceEnum.Variant]: {
    status: enumValues(VariantStatusEnum),
    stock_flag: enumValues(ProductStockFlagEnum),
  },
  [ResourceEnum.AccountingAccount]: {
    category: enumValues(AccountingAccountCategoryEnum),
    type: enumValues(AccountingAccountTypeEnum),
  },
  [ResourceEnum.AccountingTemplate]: {
    category: enumValues(AccountingTemplateCategoryEnum),
  },
  [ResourceEnum.StockMove]: {
    movement_type: enumValues(StockMoveOperationEnum),
  },
  [ResourceEnum.Product]: {
    status: enumValues(ProductStatusEnum),
    type: enumValues(ProductType),
    stock_flag: enumValues(ProductStockFlagEnum),
  },
  [ResourceEnum.Attribute]: {
    attribute_type: enumValues(AttrTypes),
  },
  [ResourceEnum.BankAccount]: {
    account_type: enumValues(BankAccountType),
  },
  [ResourceEnum.Order]: {
    status: enumValues(OrderStatusEnum),
  },
  [ResourceEnum.PriceList]: {
    status: enumValues(PriceListStatus),
  },
  [ResourceEnum.PriceListItem]: {
    status: enumValues(PriceListItemCalculateType),
    calculate_type: enumValues(CalculationType),
  },
  [ResourceEnum.Document]: {
    document_type: enumValues(DocumentTypes),
  },
  [ResourceEnum.Unit]: {
    type: enumValues(UnitTypeEnum),
  },
  [ResourceEnum.BankSepa]: {
    type: enumValues(BankSepaEnum),
  },
  [ResourceEnum.Contact]: {
    type: [...enumValues(PhoneContactType), ...enumValues(EmailContactType)],
  },
  [ResourceEnum.MarketingCompany]: {
    download_option: enumValues(DownloadCustomersOptionEnum),
    downloaded_by: enumValues(DownloadCustomersOptionEnum),
  },
  [ResourceEnum.BankTransaction]: {
    status: enumValues(BankTransactionStatusEnum),
  },
  [ResourceEnum.Marketplace]: {
    marketplace_type: enumValues(MarketplaceTypeEnum),
    marketplace_subtype: enumValues(MarketplaceSubTypeEnum),
  },
  [ResourceEnum.NotificationRoute]: {
    type: enumValues(NotificationRouteTypeEnum),
    channel: enumValues(NotificationChannel),
  },
  [ResourceEnum.RecurringDocumentSchedule]: {
    status: enumValues(RecurringScheduleStatusEnum),
    interval: enumValues(RecurringScheduleIntervalEnum),
  },
  [ResourceEnum.Carrier]: {
    carrier_type: enumValues(CarrierTypeEnum),
  },
  [ResourceEnum.Picklist]: {
    status: enumValues(PicklistStatusEnum),
  },
  [ResourceEnum.RecommendationCampaign]: {
    campaign_type: enumValues(CampaignTypeEnum),
    status: enumValues(CampaignStatusEnum),
  },
  [ResourceEnum.AnlassCampaign]: {
    anlass_type: enumValues(AnlassTypeEnum),
    status: enumValues(AnlassCampaignStatusEnum),
  },
  [ResourceEnum.NotificationSetting]: {
    channel: ["email", "sms", "whatsapp"],
    transport: [
      "brevo_transactional",
      "smtp",
      "system_mail",
      "dinstar",
      "wazzup",
    ],
    is_active: ["1", "0"],
  },
  [ResourceEnum.EpostLetter]: {
    status: [...EPOST_LETTER_STATUSES],
  },
  [ResourceEnum.NotificationMessage]: {
    channel: ["email", "sms", "whatsapp"],
    status: enumValues(NotificationDeliveryStatus),
    direction: ["outgoing", "incoming"],
  },
};

export type GetResourceFieldOptionsParams<Value = string> = {
  resource: string;
  field: string;
  t: TFunction;
  ns?: string;
  values?: Value[];
};

export type Options<Value = string> = {
  value: Value;
  label: string;
}[];

// todo: typesafe
export const getResourceFieldOptions = <Value = string>({
  resource,
  field,
  t,
  values: explicitValues,
}: GetResourceFieldOptionsParams<Value>): Options<Value> => {
  const values =
    explicitValues ?? (ENUM_VALUE_MAP[resource]?.[field] as Value[]);

  if (!values) {
    throw new Error(
      `options for [${resource}] resource field [${field}] not found`,
    );
  }

  return generateOptionsFromValuesList<Value>({
    resource,
    t,
    field,
    values,
  });
};

const generateOptionsFromValuesList = <Value = string>({
  resource,
  field,
  ns,
  t,
  values,
  getKey,
}: {
  resource: string;
  field: string;
  ns?: string;
  values: Value[];
  t: TFunction;
  getKey?: (params: { field: string; value: Value }) => string;
}): Options<Value> => {
  return values.map((value) => ({
    value,
    label: getResourceFieldOptionLabelTranslation<Value>({
      ns,
      t,
      optionValue: value,
      field,
      resource,
      getKey,
    }),
  }));
};

type OptionLabelValueParams = {
  type: "option_label";
  value: string;
};
export const getResourceFieldTranslation = ({
  resource,
  type,
  field,
  t,
  ...restParams
}: {
  resource: string;
  field: string;
  t: TFunction;
} & OptionLabelValueParams) => {
  if (type === "option_label") {
    return getResourceFieldOptionLabelTranslation({
      resource,
      field: field,
      t,
      optionValue: restParams.value,
    });
  }
  throw new Error("not implemented");
};

const getOptionLabelKeyShortPath = <Value = string>({
  value,
  field,
}: {
  value: Value;
  field: string;
}) => `field.${field}.options.${value}`;

const getOptionLabelKey = <Value = string>({
  value,
  field,
}: {
  value: Value;
  field: string;
}) => `field.${field}.options.${value}.label`;

export const getResourceFieldOptionLabelTranslation = <Value = string>({
  t,
  resource,
  field,
  ns,
  optionValue,
  getKey = getOptionLabelKey<Value>,
  getDefaultKey = getOptionLabelKeyShortPath<Value>,
}: {
  resource: string;
  field: string;
  ns?: string;
  optionValue: Value;
  t: TFunction;
  getKey?: (params: { field: string; value: Value }) => string;
  getDefaultKey?: (params: { field: string; value: Value }) => string;
}) => {
  const namespace = ns ?? getResourceI18nNamespace(resource);
  const valueFromShortPath = t(
    getDefaultKey({
      field,
      value: optionValue,
    }),
    {
      defaultValue: optionValue,
      ns: namespace,
    },
  );
  return t(
    getKey({
      field,
      value: optionValue,
    }),
    {
      ns: namespace,
      defaultValue: valueFromShortPath,
    },
  );
};
